import { DoubleRightOutlined, FilterFilled } from '@ant-design/icons'
import './AdvanceFilter.style.less'
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  Grid,
} from 'antd'
import { DATE_FORMAT } from '@/constant/date'
import {
  get,
  has,
  includes,
  isEmpty,
  map,
  omitBy,
  size,
  startCase,
} from 'lodash'
import { useQuery } from 'react-query'
import { useAuth } from '@/providers/auth.provider'
import { IsAdminRole } from '@/utils/users'
import { useEffect, useState } from 'react'
import {
  transformFieldEmptyToArray,
  transformFieldEmptyToNull,
  transformFromUrlQueryToDateRange,
  transformObjComposeKey,
} from '@/utils/common'
import FormItemOrder from '../FormItemOrder'
import { INSPECTION_PARAMS_TYPE, INSPECTION_PAYLOAD_TYPE } from '@/constant/qsf'
import { useAdvanceSelectedOrderItem } from '@/jotai/inspection.jotai'
import { TASK_PAYLOAD_TYPE } from '@/constant/task'
import { ORDER_PAYLOAD_TYPE } from '@/screens/Order/Pages/Order.constant'
import FormItemInfiniteSelect from '@/screens/Order/Pages/Landing/components/OrderTable/components/FormItemInfiniteSelect'
import { getOrderCustomFieldData } from '@/screens/Order/Pages/Landing/components/OrderTable/OrderTable.service'
import {
  getProductCustomFieldData,
  getProductSuppliersDropdown,
  getProductNumbersDropdown,
  getProductNamesDropdown,
} from '@/screens/Product/ProductTransform'

const doubleRightIcon = <DoubleRightOutlined className="close-icon" />
const filterIcon = <FilterFilled style={{ fontSize: '24px' }} />
const { yyyyMMDD } = DATE_FORMAT

const { Option } = Select
const { useBreakpoint } = Grid

type INSPECTORS_TYPE = {
  text: string
  value: string
  first_name?: string
  last_name?: string
}
type FACTORY_TYPE = {
  id: string
  name: string
}
type SUPPLIER_TYPE = {
  id: string
  name: string
  factories: FACTORY_TYPE[]
}

export interface DATASET {
  inspectors?: INSPECTORS_TYPE[]
  statuses?: { text: string; value: string }[]
  result?: any
  country?: any
  inspectionType?: any
  inspectionBrand?: any
  supplier?: SUPPLIER_TYPE[]
  factories?: any
  style_ids?: any[]
}

export interface TASK_DATA_SET {
  assignees?: INSPECTORS_TYPE[]
  priority_statuses?: any
  statuses?: any
  product_description?: string | null
  style_ids?: { text: string; value: string }[]
  section_names?: { label: string; value: string }[]
}

export interface ORDER_DATA_SET {
  delivery_date?: any
  supplier?: any[]
  factories?: any[]
  style_ids?: any[]
}

export type DRAWER_ADVANCE_FILTER_TYPE = {
  isOpen: boolean
  onClose: () => void
  onSubmit: (v: any) => any
  masterData?: any
  dataSource: DATASET & TASK_DATA_SET & ORDER_DATA_SET
  dataFilter: TASK_PAYLOAD_TYPE & INSPECTION_PAYLOAD_TYPE & ORDER_PAYLOAD_TYPE
  options?: any
  drawerMode?: 'myOrders' | 'myTasks' | 'myQuality' | 'myProducts' | ''
}

export default function DrawerAdvanceFilter(props: DRAWER_ADVANCE_FILTER_TYPE) {
  const [form] = Form.useForm()
  const { user } = useAuth()
  const isAdminRole = IsAdminRole(user)
  const { setAtomSelectedOrders, resetAtomSelectedOrders } =
    useAdvanceSelectedOrderItem()

  const {
    isOpen,
    onClose,
    dataSource,
    masterData,
    dataFilter,
    options = {},
    drawerMode = '',
  } = props
  const screens = useBreakpoint()
  const {
    inspectors: defaultInspectors = [],
    statuses: defaultStatus = [],
    result: defaultResult = [],
    country: defaultCountry = [],
    inspectionType: defaultInspectionType = [],
    inspectionBrand: defaultInspectionBrand = [],
    supplier: defaultSupplier = [],
    factories: defaultFactories = [],
    assignees: defaultTaskAssignee = [],
    priority_statuses: defaultTaskPriorityStatus = [],
    product_description: defaultProductDescription = '',
    style_ids: defaultTaskStyleIds = [],
    section_names: defaultTaskSectionNames = [],
  } = dataSource

  // STATE HANDLER
  const [stateData, setStateData] = useState(dataFilter)
  const [tempOrderSelected, setTempOrderSelected] = useState([])
  const [isEmptyFormValue, setIsEmptyFormValue] = useState(true)

  useEffect(() => {
    setStateData(dataFilter)
  }, [dataFilter])

  // FUNCTION HANDLER
  const handleSetForm = async (
    formDataSet: INSPECTION_PARAMS_TYPE &
      INSPECTION_PAYLOAD_TYPE &
      TASK_PAYLOAD_TYPE &
      ORDER_PAYLOAD_TYPE
  ) => {
    if (size(formDataSet.planned_date)) {
      formDataSet.planned_date = transformFromUrlQueryToDateRange(
        formDataSet.planned_date
      )
    }
    if (size(formDataSet.submitted_date)) {
      formDataSet.submitted_date = transformFromUrlQueryToDateRange(
        formDataSet.submitted_date
      )
    }
    if (size(formDataSet.delivery_date)) {
      formDataSet.delivery_date = transformFromUrlQueryToDateRange(
        formDataSet.delivery_date
      )
    }

    const dataFormSet = transformFieldEmptyToArray(formDataSet)

    form.setFieldsValue(dataFormSet)
  }

  const {} = useQuery(
    ['INSPECTION_ADVANCE_FILTER', stateData, isOpen],
    async () => {
      await handleSetForm(stateData)
    }
  )

  const handleUpdatedAtom = (selectedOrderIds = []) => {
    if (size(selectedOrderIds)) {
      if (size(tempOrderSelected)) {
        setAtomSelectedOrders(tempOrderSelected)
      }
    } else {
      resetAtomSelectedOrders()
    }
  }

  const handleOnSubmit = () => {
    const prepareFinalData = transformObjComposeKey(form.getFieldsValue(), {
      type: 'snakeCase',
    })
    const ordersValue = prepareFinalData['order_ids']
    const wrapData = transformFieldEmptyToNull({
      ...stateData,
      ...prepareFinalData,
    })

    handleUpdatedAtom(ordersValue)
    props.onSubmit(wrapData)
    props.onClose()
  }

  // COMPONENT HANDLER
  const renderHeader = () => {
    return (
      <Row className="title-container">
        <Row>
          <Button
            onClick={onClose}
            className="btn-back"
            icon={doubleRightIcon}
          />
        </Row>
        <Row>
          {filterIcon}
          <h1>Advanced Filters</h1>
        </Row>
      </Row>
    )
  }

  const handleRenderItemCountries = (data) => {
    const { key } = data

    return (
      <Form.Item name={key} label="Country">
        <Select
          allowClear
          showSearch
          showArrow
          placeholder="Select"
          size="large"
          mode="multiple"
          optionFilterProp="children"
        >
          {defaultCountry?.map((item) => (
            <Option key={item.id} value={item.id}>{`${item.name}`}</Option>
          ))}
        </Select>
      </Form.Item>
    )
  }

  const handleRenderItemTypes = (data) => {
    const { key } = data
    const mapData = map(defaultInspectionType, ({ id, value }) => ({
      id,
      name: value.name || '',
    }))

    return (
      <Form.Item name={key} label="Type">
        <Select
          allowClear
          showSearch
          showArrow
          placeholder="Select"
          size="large"
          mode="multiple"
          optionFilterProp="children"
        >
          {mapData?.map((item) => (
            <Option key={item.id} value={item.id}>{`${item.name}`}</Option>
          ))}
        </Select>
      </Form.Item>
    )
  }
  const handleRenderItemSupplier = (data) => {
    const { key } = data
    const { customTitle } = options
    const label = get(customTitle, `${key}.name`, startCase(key))

    if (drawerMode === 'myQuality') {
      return (
        <Form.Item name={key} label="Supplier">
          <Select
            allowClear
            showSearch
            showArrow
            placeholder="Select"
            size="large"
            mode="multiple"
            optionFilterProp="children"
          >
            {defaultSupplier?.map((item) => (
              <Option key={item.id} value={item.id}>{`${item.name}`}</Option>
            ))}
          </Select>
        </Form.Item>
      )
    }

    return (
      <FormItemInfiniteSelect
        formItemProps={{
          name: key,
          label: label,
        }}
        selectProps={{ placeholder: 'Select' }}
        isActive={isOpen}
        fetchData={(params) =>
          getProductSuppliersDropdown(
            {
              ...params,
            },
            drawerMode || 'myOrders'
          )
        }
        renderOption={(item) => item.name || item.supplier_name || item.value}
        getOptionKey={(item) => item.id || item.supplier_id || item.value}
        getOptionValue={(item) => item.id || item.supplier_id || item.value}
        searchParamName="search"
      />
    )
  }

  const handleRenderItemNumber = (data) => {
    const { key } = data
    const { customTitle } = options
    const label = get(customTitle, `${key}.name`, startCase(key))

    return (
      <FormItemInfiniteSelect
        formItemProps={{
          name: key,
          label: label,
        }}
        selectProps={{ placeholder: 'Select' }}
        isActive={isOpen}
        fetchData={(params) =>
          getProductNumbersDropdown({
            ...params,
          })
        }
        renderOption={(item) => item.number || item.name || item.value}
        getOptionKey={(item) => item.id || item.number || item.value}
        getOptionValue={(item) => item.id || item.number || item.value}
        searchParamName="search"
      />
    )
  }

  const handleRenderItemName = (data) => {
    const { key } = data
    const { customTitle } = options
    const label = get(customTitle, `${key}.name`, startCase(key))

    return (
      <FormItemInfiniteSelect
        formItemProps={{
          name: key,
          label: label,
        }}
        selectProps={{ placeholder: 'Select' }}
        isActive={isOpen}
        fetchData={(params) =>
          getProductNamesDropdown({
            ...params,
          })
        }
        renderOption={(item) => item.name || item.value}
        getOptionKey={(item) => item.id || item.name || item.value}
        getOptionValue={(item) => item.id || item.name || item.value}
        searchParamName="search"
      />
    )
  }

  const handleRenderItemFactories = (data) => {
    const { key } = data

    if (drawerMode === 'myQuality') {
      return (
        <Form.Item name={key} label="Factory">
          <Select
            allowClear
            showSearch
            showArrow
            placeholder="Select"
            size="large"
            mode="multiple"
            optionFilterProp="children"
          >
            {defaultFactories?.map((item) => (
              <Option key={item.id} value={item.id}>{`${item.name}`}</Option>
            ))}
          </Select>
        </Form.Item>
      )
    }
  }

  const handleRenderItemInspectors = (data) => {
    if (!isAdminRole) {
      return <></>
    }
    const { key } = data

    return (
      <Form.Item name={key} label="Inspector">
        <Select
          allowClear
          showSearch
          showArrow
          placeholder="Select"
          size="large"
          mode="multiple"
          optionFilterProp="children"
        >
          {defaultInspectors.map((inspector) => (
            <Option key={inspector.value} value={inspector.value}>
              {`${inspector?.first_name || ''} ${inspector?.last_name || ''}`}
            </Option>
          ))}
        </Select>
      </Form.Item>
    )
  }

  const handleRenderItemResult = (data) => {
    const { key } = data

    return (
      <Form.Item name={key} label="Result">
        <Select
          allowClear
          showSearch
          placeholder="Select"
          showArrow
          size="large"
          mode="multiple"
          optionFilterProp="children"
        >
          {defaultResult.map((value) => (
            <Option key={value.value} value={value.value}>
              {value?.text}
            </Option>
          ))}
        </Select>
      </Form.Item>
    )
  }

  const handleRenderItemStatus = (data) => {
    const { key } = data
    const { customTitle } = options
    const label = customTitle?.statuses.label ?? 'Status'

    return (
      <Form.Item name={key} label={label}>
        <Select
          allowClear
          showSearch
          showArrow
          placeholder="Select"
          size="large"
          mode="multiple"
          optionFilterProp="children"
        >
          {defaultStatus.map((value) => (
            <Option key={value.value} value={value.value}>
              {value?.text}
            </Option>
          ))}
        </Select>
      </Form.Item>
    )
  }
  const handleRenderPlannedDate = (data) => {
    const { key } = data

    return (
      <Form.Item name={key} label="Planned Date">
        <DatePicker.RangePicker
          className="w-100"
          size="large"
          format={yyyyMMDD}
        />
      </Form.Item>
    )
  }

  const handleRenderSubmittedDate = (data) => {
    const { key } = data

    return (
      <Form.Item name={key} label="Submitted Date">
        <DatePicker.RangePicker
          className="w-100"
          size="large"
          format={yyyyMMDD}
        />
      </Form.Item>
    )
  }

  const handleRenderItemOrderNo = (data) => {
    const { key } = data

    return (
      <FormItemOrder
        isActive={isOpen}
        formItemProps={{
          name: key,
          label: 'Order No.',
        }}
        selectProps={{ placeholder: 'Select' }}
        cbDataSelected={(values) => {
          setTempOrderSelected(values)
        }}
      />
    )
  }

  const handleRenderItemAssignee = (data) => {
    const { key } = data

    return (
      <Form.Item name={key} label="Assignee">
        <Select
          allowClear
          showSearch
          showArrow
          placeholder="Select"
          size="large"
          mode="multiple"
          optionFilterProp="children"
        >
          {map(defaultTaskAssignee, (value) => (
            <Option key={value.value} value={value.value}>
              {value?.text}
            </Option>
          ))}
        </Select>
      </Form.Item>
    )
  }

  const handleRenderItemPriority = (data) => {
    const { key } = data

    return (
      <Form.Item name={key} label="Priority">
        <Select
          allowClear
          showSearch
          showArrow
          placeholder="Select"
          size="large"
          mode="multiple"
          optionFilterProp="value"
        >
          {map(defaultTaskPriorityStatus, (value) => (
            <Option key={value.value} value={value.value}>
              {value?.text}
            </Option>
          ))}
        </Select>
      </Form.Item>
    )
  }

  const handleRenderItemStyleNo = (data) => {
    const { key } = data

    if (drawerMode === 'myQuality') {
      const styleIdsOptions = dataSource?.style_ids
      const mapProp = size(defaultTaskStyleIds)
        ? map(defaultTaskStyleIds, (element: any) => ({
            text: element.id,
            value: element.value,
          }))
        : map(styleIdsOptions, (element) => ({
            text: element.id,
            value: element.value,
          }))

      return (
        <Form.Item name={key} label="Style No.">
          <Select
            allowClear
            showSearch
            showArrow
            placeholder="Select"
            size="large"
            mode="multiple"
            optionFilterProp="value"
            options={mapProp}
          />
        </Form.Item>
      )
    }
  }

  const handleRenderItemProductDescription = (data) => {
    const { key } = data
    const { customTitle } = options
    const label = customTitle?.[key]?.label ?? 'Product Description'

    return (
      <Form.Item name={key} label={label}>
        <Input
          placeholder="Text"
          size="large"
          value={defaultProductDescription}
        />
      </Form.Item>
    )
  }

  const handleRenderItemBrand = (data) => {
    const { key } = data

    return (
      <Form.Item name={key} label="Brand">
        <Select
          allowClear
          showSearch
          showArrow
          placeholder="Select"
          size="large"
          mode="multiple"
          optionFilterProp="value"
          options={defaultInspectionBrand}
        />
      </Form.Item>
    )
  }

  const handleRenderDeliveryDate = (data) => {
    const { key } = data

    return (
      <Form.Item name={key} label="Delivery Date">
        <DatePicker.RangePicker
          className="w-100"
          size="large"
          format={yyyyMMDD}
          placeholder={['From', 'To']}
        />
      </Form.Item>
    )
  }

  const handleRenderGenericDate = (data) => {
    const { key } = data
    const label = startCase(key)
    const { customTitle } = options
    const customLabel = get(customTitle, `${key}.name`, label)

    return (
      <Form.Item name={key} label={customLabel}>
        <DatePicker.RangePicker
          className="w-100"
          size="large"
          format={yyyyMMDD}
          placeholder={['From', 'To']}
        />
      </Form.Item>
    )
  }

  const handleRenderTextInput = ({ key }: { key: string }) => {
    const label = startCase(key)
    const { customTitle } = options
    const customLabel = get(customTitle, `${key}.name`, label)

    return (
      <Form.Item name={key} label={customLabel}>
        <Input placeholder="Text" size="large" allowClear />
      </Form.Item>
    )
  }

  const handleRenderSectionName = (data) => {
    const { key } = data
    const { customTitle } = options
    const customLabel = get(customTitle, `${key}.label`, 'Section Name')

    return (
      <Form.Item name={key} label={customLabel}>
        <Select
          allowClear
          showSearch
          showArrow
          placeholder="Select"
          size="large"
          mode="multiple"
          optionFilterProp="label"
          options={defaultTaskSectionNames}
        />
      </Form.Item>
    )
  }

  const handleRenderCustomField = (fieldKey: string) => {
    if (includes(['search', 'query'], fieldKey)) {
      return <></>
    }

    const label = startCase(fieldKey)
    const { customTitle } = options
    const customLabel = get(customTitle, `${fieldKey}.name`, label)

    const customOptions = get(customTitle, `${fieldKey}.options`)

    if (
      customOptions &&
      Array.isArray(customOptions) &&
      customOptions.length > 0
    ) {
      return (
        <Form.Item name={fieldKey} label={customLabel}>
          <Select
            allowClear
            showSearch
            showArrow
            placeholder="Select"
            size="large"
            mode="multiple"
            optionFilterProp="children"
          >
            {customOptions.map((opt: any) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )
    }

    return (
      <FormItemInfiniteSelect
        formItemProps={{
          name: fieldKey,
          label: customLabel,
        }}
        selectProps={{ placeholder: 'Select' }}
        isActive={isOpen}
        fetchData={(params) => {
          if (drawerMode === 'myProducts') {
            return getProductCustomFieldData({
              ...params,
              field: fieldKey,
              mode: drawerMode,
            })
          }

          // TODO: Return with {text, value} follow by the antd component
          return getOrderCustomFieldData({
            ...params,
            field: fieldKey,
            mode: drawerMode,
          })
        }}
        renderOption={(item) =>
          item.property_value ||
          item.text ||
          item.supplier_name ||
          item.factory_name ||
          item.pv_value_str
        }
        getOptionKey={(item) =>
          item.property_value ||
          item.id ||
          item.supplier_id ||
          item.factory_id ||
          item.pv_value_str
        }
        getOptionValue={(item) =>
          item.property_value ||
          item.id ||
          item.supplier_id ||
          item.factory_id ||
          item.pv_value_str
        }
        searchParamName="search"
      />
    )
  }

  const handleRenderFormItems = () => {
    const dynamicFields: any = {
      countries: handleRenderItemCountries,
      inspection_type_ids: handleRenderItemTypes,
      supplier: handleRenderItemSupplier,
      suppliers: handleRenderItemSupplier,
      factories: handleRenderItemFactories,
      inspectors: handleRenderItemInspectors,
      result: handleRenderItemResult,
      status: handleRenderItemStatus, // For Inspection
      statuses: handleRenderItemStatus, // For Task and Inspection
      brands: handleRenderItemBrand,
      planned_date: handleRenderPlannedDate,
      submitted_date: handleRenderSubmittedDate,
      assignees: handleRenderItemAssignee,
      priority_statuses: handleRenderItemPriority,
      product_description: handleRenderItemProductDescription,
      style_ids: handleRenderItemStyleNo,
      section_names: handleRenderSectionName,
      delivery_date: handleRenderDeliveryDate,
      order_ids: handleRenderItemOrderNo,
      number: handleRenderItemNumber,
      name: handleRenderItemName,
    }

    const renderAllFilterColumn = map(stateData, (value, key) => {
      const getKey = has(dynamicFields, key)

      if (getKey && get(dynamicFields, key)({ key })) {
        return get(dynamicFields, key)({ key })
      }
      const { customTitle } = options
      const fieldType = get(customTitle, `${key}.type`)

      if (fieldType === 'date') {
        return handleRenderGenericDate({ key })
      }

      if (fieldType === 'text') {
        return handleRenderTextInput({ key })
      }

      return handleRenderCustomField(key)
    })

    return renderAllFilterColumn
  }

  const renderFilterForm = () => {
    const handleFieldChange = (_, fieldProps) => {
      const mapVal = map(fieldProps, 'value')
      setIsEmptyFormValue(!size(omitBy(mapVal, isEmpty)))
    }

    return (
      <Form form={form} layout="vertical" onFieldsChange={handleFieldChange}>
        {handleRenderFormItems()}
      </Form>
    )
  }

  const renderGroupButton = () => {
    return (
      <Row className="wrap-group-btn">
        <Button className="btn-cancel" onClick={props.onClose} size="large">
          Cancel
        </Button>
        <Button
          // disabled={isEmptyFormValue} // TODO later
          className="btn-save"
          onClick={() => {
            handleOnSubmit()
          }}
          size="large"
          type="primary"
        >
          Apply
        </Button>
      </Row>
    )
  }

  return (
    <Drawer
      title=""
      placement={'right'}
      closable={false}
      open={isOpen}
      key={'right'}
      width={screens.xs ? '100%' : 550}
    >
      {renderHeader()}
      <div className="wrap-filter-form">{renderFilterForm()}</div>
      {renderGroupButton()}
    </Drawer>
  )
}
